// ItemEditForm — Detail Panel "수정" 모드 폼.
//
// 인라인 편집(값 클릭)과 별개로, 이름·그룹·타입별 전체 필드를 한 번에 고치는 폼.
// - 비어 있어 보기 모드에서 숨겨진 선택 필드(URL·메모 등)도 여기서 채울 수 있다.
// - 필드 정의는 constants/itemFields.ts (서버 스키마와 1:1, 등록 폼과 공유).
// - 저장: updateItem 한 번 호출 (name + group_id + payload + tags 전체 교체).
//   폼에 없는 payload 키(type 등)는 그대로 보존하고, 값은 서버 형식으로 변환한다.
// - Esc: 수정 취소 (패널 닫힘으로 번지지 않도록 전파 차단)

import { useCallback, useState } from 'react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Button } from '../../ui/Button';
import { useItemMutations } from '../../hooks/useItems';
import { useGroupsTree } from '../../hooks/useGroupsTree';
import { useToast } from '../Toast';
import { formatApiError } from '../../api/client';
import { flattenGroups } from '../NewItemPanel';
import { ItemFieldInput } from '../ItemFieldInput';
import { TagChipInput } from '../TagChipInput';
import { buildPayload, getItemFields, toFormString } from '../../constants/itemFields';
import type { ItemMeta } from '../../api/items';

interface ItemEditFormProps {
  item: ItemMeta;
  payload: Record<string, unknown>;
  /** 현재 태그 이름 목록 */
  tags: string[];
  /** 저장 성공 또는 취소 시 보기 모드로 복귀 */
  onDone: () => void;
}

/**
 * 항목 수정 폼.
 * 이름·그룹·타입별 payload 필드를 편집하고 저장/취소한다.
 */
export function ItemEditForm({ item, payload, tags: initialTags, onDone }: ItemEditFormProps) {
  const { updateItem } = useItemMutations();
  const toast = useToast();
  const { data: groupTree = [] } = useGroupsTree('credential');
  const groupOptions = flattenGroups(groupTree);

  const fields = getItemFields(item.type);

  const [name, setName] = useState(item.name);
  const [groupId, setGroupId] = useState(item.group_id !== null ? String(item.group_id) : '');
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, toFormString(f, payload[f.key])])),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tags, setTags] = useState<string[]>(initialTags);

  const setValue = useCallback((key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    // 필수·형식 검사 + 서버 형식 변환 (폼 밖 키는 보존)
    const built = buildPayload(fields, values, payload);
    const errs = { ...built.errors };
    if (!name.trim()) errs['__name'] = '이름은 필수입니다';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      await updateItem.mutateAsync({
        id: item.id,
        patch: {
          name: name.trim(),
          group_id: groupId ? Number(groupId) : null,
          payload: built.payload,
          tags,
        },
      });
      toast.success('저장되었습니다');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err, '저장 실패'));
    }
  }, [name, groupId, fields, values, payload, tags, updateItem, item.id, toast, onDone]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // 하위 입력(태그 칩 입력 Enter·Esc 등)이 이미 처리한 키는 무시 — Enter가 폼 저장으로 번지지 않게
    if (e.defaultPrevented) return;
    if (e.key === 'Escape') {
      // 패널 Esc 닫기 리스너(document)로 번지지 않게 차단 — 수정 취소만
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      onDone();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      void handleSave();
    }
  };

  return (
    <div className="item-edit-form" onKeyDown={handleKeyDown}>
      <Input
        label="이름"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          setErrors((prev) => {
            const n = { ...prev };
            delete n['__name'];
            return n;
          });
        }}
        error={errors['__name']}
        autoComplete="off"
        autoFocus
      />

      <Select label="그룹" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
        <option value="">그룹 없음 (미분류)</option>
        {groupOptions.map((g) => (
          <option key={g.id} value={String(g.id)}>
            {g.label}
          </option>
        ))}
      </Select>

      {fields.map((f) => (
        <ItemFieldInput
          key={f.key}
          field={f}
          value={values[f.key] ?? ''}
          onChange={(v) => setValue(f.key, v)}
          error={errors[f.key]}
          idPrefix="edit"
        />
      ))}

      <div className="ui-input-wrap">
        <span className="ui-input-label">태그</span>
        <TagChipInput value={tags} onChange={setTags} placeholder="태그 추가 (Enter)" />
      </div>

      <div className="item-edit-form__actions">
        <Button variant="ghost" size="sm" onClick={onDone}>
          취소
        </Button>
        <Button variant="primary" size="sm" loading={updateItem.isPending} onClick={() => void handleSave()}>
          저장
        </Button>
      </div>
    </div>
  );
}
